import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContextDocument,
  deleteContextDocument,
  listContextDocuments,
  updateContextDocument,
  uploadContextDocument,
  type ContextCreateInput,
  type ContextDocument,
  type ContextUpdateInput,
} from './context-api'

export const contextKeys = {
  all: ['context'] as const,
  documents: () => [...contextKeys.all, 'documents'] as const,
}

export function useContextDocuments() {
  return useQuery({
    queryKey: contextKeys.documents(),
    queryFn: () => listContextDocuments(),
    staleTime: 30_000,
  })
}

export function useCreateContextDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ContextCreateInput) => createContextDocument(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextKeys.documents() })
    },
  })
}

export function useUpdateContextDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ContextUpdateInput }) =>
      updateContextDocument(id, input),
    onSuccess: (doc: ContextDocument) => {
      void qc.invalidateQueries({ queryKey: contextKeys.documents() })
      void doc
    },
  })
}

export function useDeleteContextDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteContextDocument(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextKeys.documents() })
    },
  })
}

export function useUploadContextDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      title,
      document_type,
    }: {
      file: File
      title?: string
      document_type?: 'company' | 'user' | 'project' | 'general'
    }) =>
      uploadContextDocument(file, {
        title,
        document_type,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contextKeys.documents() })
    },
  })
}
